import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import lodashWithout from 'lodash/fp/without';
import lodashUniq from 'lodash/fp/uniq';
import queryString from 'query-string';
import typeCheck from 'common/util/typeCheck';

import { UTILITY_USER_ROLE_ADMIN } from 'common/utility/userRoles';

import {
  UTILITY_PROVIDERS_MILES_RANGE,
  UTILITY_PROVIDER_BLOCKED_MANUAL_MAPPING_NAMES,
} from 'common/utility/constants';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_GET,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
  SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
  SR_API_ACTION_UTILITY_PROVIDER_UPDATE_ADDRESS_COVERAGE,
  SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
  SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
  SR_API_ACTION_ADDRESS_FCC_CONFIRM_MAPPING,
} from 'api/utilityActionTypes';

import { SCREEN_ID_UTILITY_PROFIT_ADMIN_PROCESSING_ACTIVATION } from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import { getActivationLoadErrorView } from 'client/helpers/activationLoadError';

import branch from 'client/core/branch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import OnSwitchActions from 'client/actions/OnSwitch';
import UtilityActivationActions from 'client/actions/UtilityActivation';
import UtilityProviderActions from 'client/actions/UtilityProvider';
import UtilityDealerActions from 'client/actions/UtilityDealer';
import UtilityUserActions from 'client/actions/UtilityUser';
import { getRangeIdentifier } from 'client/actions/UtilityProvider/getMappedProvidersInRange';

import SrUtilityProfitAdminProcessActivationPage from 'client/components/SrUtilityProfitAdminProcessActivationPage/SrUtilityProfitAdminProcessActivationPage';
import ActivationLoadError from 'client/components/ActivationLoadError/ActivationLoadError';
import UPProviderSearchForm from 'client/components/UPProviderSearchForm/UPProviderSearchForm';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import { getManualMappingBlockedProviders } from 'common/utility/helpers';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PROFIT_ADMIN_PROCESSING_ACTIVATION)
@withScrollToTop
@withApiState(({ match }) => ({
  getUtilityActivationApiState: {
    action: SR_API_ACTION_UTILITY_ACTIVATION_GET,
    queryId: match.params.utilityActivationShortId,
  },
}))
@branch(({ match }) => ({
  currentUser: ['utilityUser'],
  utilityActivation: ['utilityActivations', 'byShortId', match.params.utilityActivationShortId],
  utilityDealers: ['utilityDealers', 'list'],
  utilitySalesReps: ['utilitySalesReps', 'list'],
}))
@branch(({ utilityActivation }) => {
  if (!utilityActivation) {
    return {};
  }

  const result = {};

  // Get utility types based on partnerId
  const partnerId = utilityActivation.utilityPartnerId;
  result.utilityTypes = partnerId
    ? ['utilityActivations', 'typesByPartnerId', partnerId]
    : ['utilityActivations', 'types'];

  if (utilityActivation.address?.id) {
    result.utilityProvidersByAddress = [
      'utilityProviders',
      'byAddressId',
      utilityActivation.address.id,
    ];
  }

  if (utilityActivation.address?.latitude != null && utilityActivation.address?.longitude != null) {
    const rangeIdentifier = getRangeIdentifier({
      latitude: utilityActivation.address.latitude,
      longitude: utilityActivation.address.longitude,
      rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
    });
    result.utilityProvidersInRange = ['utilityProviders', 'coverageByRange', rangeIdentifier];
  }

  if (utilityActivation.onSwitchOrderId) {
    result.workflowContext = [
      'onSwitch',
      'workflowContextByOrderId',
      utilityActivation.onSwitchOrderId,
    ];
  }

  return result;
})
class UtilityProfitAdminProcessingActivationPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Profit Admin | Process Activation',
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    utilityActivation: PropTypes.object,
    utilityDealers: PropTypes.array,
    utilityTypes: PropTypes.array,
    utilityProvidersInRange: PropTypes.array,
    workflowContext: PropTypes.object,
    getUtilityActivationApiState: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return Promise.all([
      UtilityDealerActions.getList(inject),
      UtilityUserActions.getSalesRepList(inject),
      UtilityActivationActions.getByShortId(inject, {
        utilityActivationShortId: match.params.utilityActivationShortId,
      }).then((utilityActivation) => {
        const promises = [];

        // Fetch types with partnerId from the activation
        const partnerId = utilityActivation?.utilityPartnerId;
        promises.push(UtilityActivationActions.getTypesList(inject, { partnerId }));

        if (utilityActivation?.address) {
          promises.push(
            UtilityProviderActions.getMappedProvidersInRange(inject, {
              latitude: utilityActivation.address.latitude,
              longitude: utilityActivation.address.longitude,
              rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
            }),
          );
          promises.push(
            UtilityProviderActions.getListByAddressId(inject, {
              addressId: utilityActivation.address.id,
            }),
          );
        }

        // Fetch OnSwitch workflow context if available
        if (utilityActivation?.onSwitchOrderId) {
          promises.push(
            OnSwitchActions.getOrderWorkflowContext(inject, {
              onSwitchOrderId: utilityActivation.onSwitchOrderId,
            }),
          );
        }

        return Promise.all(promises);
      }),
    ]);
  }

  constructor(props) {
    super(props);
    this.state = {
      serviceAreaProviderMappingArgs: null,
      serviceAreaProviderMappingIsAdding: false,
      serviceAreaMappedProvidersByTypeId: {},
      manualMappingBlockModalArgs: null,
    };
  }

  appendServiceAreaMappedProvider = ({ utilityTypeId, utilityProvider }) => {
    if (!utilityTypeId || !utilityProvider?.id) {
      return;
    }

    this.setState((prevState) => {
      const existingProviders = prevState.serviceAreaMappedProvidersByTypeId[utilityTypeId] || [];
      const hasProvider = existingProviders.some(({ id }) => id === utilityProvider.id);

      if (hasProvider) {
        return null;
      }

      return {
        serviceAreaMappedProvidersByTypeId: {
          ...prevState.serviceAreaMappedProvidersByTypeId,
          [utilityTypeId]: [...existingProviders, utilityProvider],
        },
      };
    });
  };

  updateProviderCoveragePoints = ({
    addressId,
    utilityTypeId,
    utilityProviderIds,
    coveragePointsConfig,
    mappingNotes,
    manualMappingBlockBypassed = false,
  }) => {
    typeCheck('addressId::PositiveNumber', addressId);
    typeCheck('utilityProviderIds::Array', utilityProviderIds);
    typeCheck('utilityTypeId::PositiveNumber', utilityTypeId);
    typeCheck('coveragePointsConfig::Maybe Array', coveragePointsConfig);
    typeCheck('mappingNotes::Maybe String');

    if (!manualMappingBlockBypassed) {
      const { utilityActivation } = this.props;
      const currentItem = (utilityActivation?.items || []).find(
        (item) => item.utilityTypeId === utilityTypeId,
      );
      const currentProviderIds = (currentItem?.availableProviders || []).map(({ id }) => id);

      const blockedProviderIds = getManualMappingBlockedProviders({
        utilityTypeId,
        currentProviderIds,
        newProviderIds: utilityProviderIds,
      });

      if (blockedProviderIds.length > 0) {
        const blockedProviderActions = Object.fromEntries(
          blockedProviderIds.map((id) => [
            id,
            utilityProviderIds.includes(id) ? 'Mapping' : 'Unmapping',
          ]),
        );
        this.setState({
          manualMappingBlockModalArgs: {
            addressId,
            utilityTypeId,
            utilityProviderIds,
            coveragePointsConfig,
            mappingNotes,
            blockedProviderIds,
            blockedProviderActions,
          },
        });
        return Promise.resolve({ wasBlocked: true });
      }
    }

    const { dispatch } = this.props;
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PROVIDER_UPDATE_ADDRESS_COVERAGE,
      payload: {
        addressId,
        utilityTypeId,
        utilityProviderIds,
        coveragePointsConfig,
        mappingNotes,
        manualMappingBlockBypassed: manualMappingBlockBypassed || undefined,
      },
      onError(error) {
        global.alert(error.message);
      },
    }).then(() => {
      return Promise.all([
        this.reloadUtilityActivation(),
        dispatch(UtilityProviderActions.getListByAddressId, {
          addressId,
          refresh: true,
        }),
      ]);
    });
  };

  closeManualMappingBlockModal = () => {
    this.setState({ manualMappingBlockModalArgs: null });
  };

  proceedWithBypassedMapping = () => {
    const args = this.state.manualMappingBlockModalArgs;
    this.setState({ manualMappingBlockModalArgs: null });
    if (!args) return;
    this.updateProviderCoveragePoints({ ...args, manualMappingBlockBypassed: true });
  };

  updateActivation = (payload) => {
    const { dispatch } = this.props;

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
      payload,
      onError(error) {
        global.alert(error.message);
      },
    }).then(this.reloadUtilityActivation);
  };

  updateActivationItem = (payload) => {
    const { dispatch } = this.props;
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
      payload,
      onError(error) {
        global.alert(error.message);
      },
    }).then(this.reloadUtilityActivation);
  };

  toggleRequiredType = (utilityTypeId) => {
    const { utilityActivation, utilityTypes, dispatch } = this.props;

    const utilityType = utilityTypes.find(({ id }) => id === utilityTypeId);
    const requiredTypes = utilityActivation.requiredTypes.map(({ utilityTypeId }) => utilityTypeId);
    const isRemovingType = requiredTypes.includes(utilityTypeId);

    const newUtilityTypeIds = isRemovingType
      ? lodashWithout([utilityTypeId], requiredTypes)
      : lodashUniq([...requiredTypes, utilityTypeId]);

    const confirmMessage = `Are you sure you want to mark "${utilityType.label}" as ${
      isRemovingType ? 'not ' : ''
    }required?`;

    if (!global.confirm(confirmMessage)) {
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
      payload: {
        addressId: utilityActivation.addressId,
        utilityTypeIds: newUtilityTypeIds,
      },
      onError(error) {
        global.alert(error.message);
      },
    }).then(this.reloadUtilityActivation);
  };

  onMapServiceAreaProvider = ({ utilityServiceAreaId, utilityTypeId, companyName, addressId }) => {
    this.setState({
      serviceAreaProviderMappingArgs: {
        utilityServiceAreaId,
        utilityTypeId,
        companyName,
        addressId,
      },
      serviceAreaProviderMappingIsAdding: false,
    });
  };

  onMapServiceAreaProviderSelected = (utilityProvider) => {
    const { dispatch } = this.props;
    const { serviceAreaProviderMappingArgs } = this.state;

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
      payload: {
        utilityServiceAreaId: serviceAreaProviderMappingArgs.utilityServiceAreaId,
        utilityProviderId: utilityProvider.id,
      },
      onSuccess: () => {
        this.appendServiceAreaMappedProvider({
          utilityTypeId: serviceAreaProviderMappingArgs.utilityTypeId,
          utilityProvider,
        });
        this.reloadUtilityActivation();
        this.onCloseMapServiceAreaProvider();
      },
    });
  };

  onMapServiceAreaProviderAddNew = () => {
    this.setState({
      serviceAreaProviderMappingIsAdding: true,
    });
  };

  onMapServiceAreaProviderAddNewSubmit = (utilityProviderUpsertForm) => {
    const { dispatch } = this.props;
    const { serviceAreaProviderMappingArgs } = this.state;

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
      payload: utilityProviderUpsertForm,
    }).then(({ utilityProvider }) => {
      return dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
        payload: {
          utilityServiceAreaId: serviceAreaProviderMappingArgs.utilityServiceAreaId,
          utilityProviderId: utilityProvider.id,
        },
        onSuccess: () => {
          this.appendServiceAreaMappedProvider({
            utilityTypeId: serviceAreaProviderMappingArgs.utilityTypeId,
            utilityProvider,
          });
          this.reloadUtilityActivation();
          this.onCloseMapServiceAreaProvider();
        },
      });
    });
  };

  onCloseMapServiceAreaProvider = () => {
    this.setState({
      serviceAreaProviderMappingArgs: null,
      serviceAreaProviderMappingIsAdding: false,
    });
  };

  reloadUtilityActivation = () => {
    const { dispatch, match } = this.props;
    return Promise.all([
      dispatch(UtilityActivationActions.getByShortId, {
        utilityActivationShortId: match.params.utilityActivationShortId,
        forceRefresh: true,
      }).then((utilityActivation) => {
        if (utilityActivation?.address) {
          dispatch(UtilityProviderActions.getMappedProvidersInRange, {
            latitude: utilityActivation.address.latitude,
            longitude: utilityActivation.address.longitude,
            rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
            utilityTypeId: null,
          });
        }
      }),
    ]);
  };

  onConfirmMapping = ({ addressId, utilityTypeId }) => {
    const { dispatch } = this.props;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_FCC_CONFIRM_MAPPING,
      payload: {
        addressId,
        utilityTypeId,
      },
      onSuccess: () => {
        this.reloadUtilityActivation();
      },
    });
  };

  static _getBlockedProviderLabel(blockedProviderIds) {
    return blockedProviderIds
      .map((id) => UTILITY_PROVIDER_BLOCKED_MANUAL_MAPPING_NAMES[id])
      .filter(Boolean)
      .join(' and ');
  }

  static _getBlockedProviderTitle(blockedProviderIds, blockedProviderActions) {
    const label = UtilityProfitAdminProcessingActivationPage._getBlockedProviderLabel(blockedProviderIds);
    const actionValues = blockedProviderIds.map((id) => blockedProviderActions[id]);
    const action = actionValues.every((a) => a === actionValues[0]) ? actionValues[0] : 'Mapping/Unmapping';
    return `${action} ${label}`;
  }

  render() {
    const {
      currentUser,
      utilityActivation,
      utilityProvidersInRange,
      utilityProvidersByAddress,
      utilityTypes,
      utilityDealers,
      utilitySalesReps,
      workflowContext,
      getUtilityActivationApiState,
      isTransitioning,
      match,
      location,
    } = this.props;

    const queryParams = queryString.parse(location.search || '');

    const {
      serviceAreaProviderMappingArgs,
      serviceAreaProviderMappingIsAdding,
      serviceAreaMappedProvidersByTypeId,
      manualMappingBlockModalArgs,
    } = this.state;

    const errorView = getActivationLoadErrorView({
      apiState: getUtilityActivationApiState,
      variant: 'admin',
      backTo: queryParams?.backUrl || '/utility-profit/admin',
      backLabel: 'Back',
    });

    if (errorView.isError && !utilityActivation) {
      if (errorView.shouldRedirectToLogin) {
        return <Redirect to={`/utility-profit/login?next=${encodeURIComponent(location.pathname)}`} />;
      }
      return (
        <ActivationLoadError
          message={errorView.message}
          referenceId={errorView.referenceId}
          backTo={errorView.backTo}
          backLabel={errorView.backLabel}
        />
      );
    }

    if (
      isTransitioning ||
      !utilityTypes ||
      !utilityDealers ||
      !utilitySalesReps ||
      (getUtilityActivationApiState.inProgress && !utilityActivation)
    ) {
      return <div className="loading-ellipses" />;
    }

    if (!utilityActivation) {
      return (
        <div>
          {`Utility Activation with short ID ${match.params.utilityActivationShortId} not found.`}
        </div>
      );
    }

    return (
      <>
        <SrUtilityProfitAdminProcessActivationPage
          currentUser={currentUser}
          utilityProvidersInRange={utilityProvidersInRange}
          utilityProvidersByAddress={utilityProvidersByAddress}
          utilityTypes={utilityTypes}
          utilityDealers={utilityDealers}
          utilitySalesReps={utilitySalesReps}
          utilityActivation={utilityActivation}
          updateProviderCoveragePoints={this.updateProviderCoveragePoints}
          updateActivation={this.updateActivation}
          updateActivationItem={this.updateActivationItem}
          toggleRequiredType={this.toggleRequiredType}
          onMapServiceAreaProvider={this.onMapServiceAreaProvider}
          onConfirmMapping={this.onConfirmMapping}
          serviceAreaMappedProvidersByTypeId={serviceAreaMappedProvidersByTypeId}
          backTitle={queryParams?.backTitle || null}
          backUrl={queryParams?.backUrl || null}
          workflowContext={workflowContext}
        />
        {serviceAreaProviderMappingArgs && (
          <UPProviderSearchForm
            utilityType={utilityTypes.find(
              ({ id }) => serviceAreaProviderMappingArgs.utilityTypeId === id,
            )}
            addressDisplay={serviceAreaProviderMappingArgs.companyName}
            onProviderSelected={this.onMapServiceAreaProviderSelected}
            onClose={this.onCloseMapServiceAreaProvider}
            onAddNew={this.onMapServiceAreaProviderAddNew}
          />
        )}
        {serviceAreaProviderMappingIsAdding && (
          <UPProviderUpsertForm
            title="Connect to a Provider"
            utilityType={utilityTypes.find(
              ({ id }) => serviceAreaProviderMappingArgs.utilityTypeId === id,
            )}
            initialValues={{
              addressId: serviceAreaProviderMappingArgs.addressId,
              utilityTypeId: serviceAreaProviderMappingArgs.utilityTypeId,
            }}
            addressDisplay={serviceAreaProviderMappingArgs.companyName}
            onCancel={this.onCloseMapServiceAreaProvider}
            onSubmit={this.onMapServiceAreaProviderAddNewSubmit}
          />
        )}
        {manualMappingBlockModalArgs && (
          <SrSimpleMessageModal
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            headline={UtilityProfitAdminProcessingActivationPage._getBlockedProviderTitle(
              manualMappingBlockModalArgs.blockedProviderIds,
              manualMappingBlockModalArgs.blockedProviderActions,
            )}
            message={
              <>
                {`${UtilityProfitAdminProcessingActivationPage._getBlockedProviderLabel(
                  manualMappingBlockModalArgs.blockedProviderIds,
                )} ${manualMappingBlockModalArgs.blockedProviderIds.length > 1 ? 'are' : 'is'} managed exclusively by the automated eligibility API.`}
                <br />
                Manual changes are not recommended.
              </>
            }
            onClose={this.closeManualMappingBlockModal}
            customCtas={[
              <div key="btns" style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    hasBoxShadow
                    onClick={this.proceedWithBypassedMapping}
                  >
                    Proceed anyway
                  </SrButton>
                </div>
                <div style={{ flex: 1 }}>
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_GREY}
                    hasBoxShadow
                    onClick={this.closeManualMappingBlockModal}
                  >
                    Cancel
                  </SrButton>
                </div>
              </div>,
            ]}
          />
        )}
      </>
    );
  }
}

UtilityProfitAdminProcessingActivationPage.displayName =
  'UtilityProfitAdminProcessingActivationPage';

export default UtilityProfitAdminProcessingActivationPage;
